<template>
	<div class="loan-history-tab container-fluid px-0 akkurate-small">
		<div class="mb-3 border-bottom pb-2">
			<h6 class="m-0 fw-bold akkurate-green">Loan history</h6>
			<p class="mb-0 text-muted mt-1 small">
				Approvals, adjustments, and write-offs (not repayments). Newest first.
			</p>
		</div>

		<div v-if="listError" class="alert alert-danger py-2 small mb-2">
			{{ listError }}
		</div>

		<div
			v-if="listLoading && events.length === 0"
			class="text-center text-muted py-4"
		>
			<b-spinner small class="align-middle me-2" />
			Loading history…
		</div>

		<div
			v-else-if="!listLoading && events.length === 0 && !listError"
			class="text-center text-muted py-4"
		>
			No history events yet.
		</div>

		<ul v-else class="list-unstyled mb-0">
			<li
				v-for="(ev, idx) in events"
				:key="historyKey(ev, idx)"
				class="border-start border-3 ps-3 pb-3 ms-1"
				:class="borderClass(ev.event_type)"
			>
				<div class="d-flex flex-wrap align-items-center gap-2 mb-1">
					<span class="badge" :class="badgeClass(ev.event_type)">{{
						formatEventType(ev.event_type)
					}}</span>
					<font-awesome-icon
						:icon="iconFor(ev.event_type)"
						class="text-muted"
					/>
					<span class="text-muted">{{
						formatDate(ev.occurred_at)
					}}</span>
				</div>
				<p class="fw-semibold mb-1">{{ ev.title || "—" }}</p>
				<div
					v-if="ev.event_type === 'write_off'"
					class="text-muted extra-small mb-1"
				>
					Date shown may be approximate.
				</div>
				<div class="bg-light rounded p-2 small">
					<template v-if="ev.detail && typeof ev.detail === 'object'">
						<dl class="row mb-0 small">
							<template v-for="(val, key) in flatDetail(ev)">
								<dt class="col-sm-4 text-muted mb-1">{{ key }}</dt>
								<dd class="col-sm-8 mb-1">{{ val }}</dd>
							</template>
						</dl>
					</template>
					<span v-else class="text-muted">—</span>
				</div>
			</li>
		</ul>

		<p
			v-if="meta && (meta.loan_id != null || meta.loan_number)"
			class="text-muted mt-3 mb-0 extra-small"
		>
			Loan
			<span v-if="meta.loan_number"
				>#{{ meta.loan_number }}</span
			>
			<span v-if="meta.loan_id != null"> (id {{ meta.loan_id }})</span>
		</p>
	</div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { apiErrorMessage } from "@/utils/apiErrorMessage.js";
import { loanHistoryPath } from "@/utils/loanViewApiPaths.js";

export default {
	name: "LoanHistoryTab",
	props: {
		loanRef: {
			type: [Number, String],
			default: null,
		},
	},
	data() {
		return {
			events: [],
			meta: null,
			listLoading: false,
			listError: null,
		};
	},
	computed: {
		effectiveLoanRef() {
			if (this.loanRef == null || this.loanRef === "") {
				return null;
			}
			if (typeof this.loanRef === "number") {
				return Number.isFinite(this.loanRef) && this.loanRef > 0
					? this.loanRef
					: null;
			}
			const s = String(this.loanRef).trim();
			return s ? s : null;
		},
	},
	watch: {
		effectiveLoanRef: {
			immediate: true,
			handler(ref) {
				if (ref != null) {
					this.fetchHistory();
				} else {
					this.events = [];
					this.meta = null;
					this.listError = null;
				}
			},
		},
	},
	methods: {
		historyKey(ev, idx) {
			if (ev && ev.occurred_at != null && ev.title) {
				return `${ev.occurred_at}-${ev.title}-${idx}`;
			}
			return `h-${idx}`;
		},
		formatEventType(t) {
			if (t === "approval") {
				return "Approval";
			}
			if (t === "adjustment") {
				return "Adjustment";
			}
			if (t === "write_off") {
				return "Write-off";
			}
			return t ? String(t) : "Event";
		},
		badgeClass(t) {
			if (t === "approval") {
				return "bg-success";
			}
			if (t === "adjustment") {
				return "bg-info text-dark";
			}
			if (t === "write_off") {
				return "bg-warning text-dark";
			}
			return "bg-secondary";
		},
		borderClass(t) {
			if (t === "approval") {
				return "border-success";
			}
			if (t === "adjustment") {
				return "border-info";
			}
			if (t === "write_off") {
				return "border-warning";
			}
			return "border-secondary";
		},
		iconFor(t) {
			if (t === "approval") {
				return ["fas", "check-circle"];
			}
			if (t === "adjustment") {
				return ["fas", "sliders-h"];
			}
			if (t === "write_off") {
				return ["fas", "file-invoice"];
			}
			return ["fas", "circle"];
		},
		formatDate(raw) {
			if (raw == null || raw === "") {
				return "";
			}
			const d = new Date(raw);
			return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
		},
		flatDetail(ev) {
			const d = ev && ev.detail;
			if (!d || typeof d !== "object") {
				return {};
			}
			const out = {};
			const skip = new Set(["__proto__", "constructor"]);
			Object.keys(d).forEach((k) => {
				if (skip.has(k)) {
					return;
				}
				let v = d[k];
				if (v != null && typeof v === "object") {
					v = JSON.stringify(v);
				}
				out[humanizeKey(k)] = v === null || v === "" ? "—" : String(v);
			});
			return out;
		},
		async fetchHistory() {
			const ref = this.effectiveLoanRef;
			if (ref == null) {
				return;
			}
			this.listLoading = true;
			this.listError = null;
			try {
				const response = await ApiService.get(loanHistoryPath(ref));
				const root = response && response.data;
				const rows = Array.isArray(root.data)
					? root.data
					: root && Array.isArray(root)
						? root
						: [];
				this.events = rows;
				this.meta =
					root && root.meta && typeof root.meta === "object"
						? root.meta
						: null;
			} catch (error) {
				this.events = [];
				this.meta = null;
				this.listError = apiErrorMessage(
					error,
					"Could not load loan history.",
				);
			} finally {
				this.listLoading = false;
			}
		},
	},
};

function humanizeKey(k) {
	return String(k)
		.replace(/_/g, " ")
		.replace(/\b\w/g, (c) => c.toUpperCase());
}
</script>

<style scoped>
.extra-small {
	font-size: 0.75rem;
}
</style>
